/**
 * useApi — authenticated fetch hook.
 *
 * Attaches the Clerk Bearer token to every request.
 * Drop-in replacement for plain fetch() calls.
 *
 * Usage:
 *   const { authFetch } = useApi();
 *
 *   // GET
 *   const data = await authFetch('/api/zendesk/tickets');
 *
 *   // POST
 *   const data = await authFetch('/api/zendesk/import-tickets', {
 *     method: 'POST',
 *     body: JSON.stringify({ days_back: 30 }),
 *   });
 *
 *   // DELETE
 *   const data = await authFetch('/api/zendesk/tickets/clear', { method: 'DELETE' });
 *
 * Error shape on non-2xx responses:
 *   err.status  — HTTP status code (e.g. 402, 404, 500)
 *   err.detail  — raw detail value from the API (string or object)
 *   err.message — always a string (object details are JSON-serialized)
 *
 * 402 payloads have err.detail = { code, message, used, limit, tier }
 * Callers check: if (err.status === 402) { openUpgradeModal() }
 */

import { useAuth } from '@clerk/react';
import { useCallback } from 'react';

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

export function useApi() {
  const { getToken } = useAuth();

  const authFetch = useCallback(async (path, options = {}) => {
    const token = await getToken();

    const headers = {
      'Content-Type': 'application/json',
      ...(options.headers || {}),
      'Authorization': `Bearer ${token}`,
    };

    const response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const body = await response.json().catch(() => ({ detail: 'Request failed' }));
      // detail can be a string or a structured object (e.g. 402 limit payloads)
      const err = new Error(
        typeof body.detail === 'string'
          ? body.detail
          : (body.detail?.message || JSON.stringify(body.detail) || `Request failed: ${response.status}`)
      );
      err.status = response.status;
      // Attach raw detail so callers can inspect without re-parsing
      err.detail = body.detail;
      throw err;
    }

    // 204 No Content (e.g. DELETE) returns an empty body — skip JSON parsing
    if (response.status === 204) return null;
    return response.json();
  }, [getToken]);

  return { authFetch };
}
